import React from 'react';
import { Card, Descriptions, Tag, Tabs, Button, Space, Image, Empty, Typography, Row, Col, Statistic, List, Popconfirm } from 'antd';
import { EditOutlined, UserOutlined, TeamOutlined, BankOutlined, EnvironmentOutlined, FileTextOutlined, CameraOutlined, ContactsOutlined, ApartmentOutlined, EyeOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { CustomerDetailDTO, SelectFieldOption } from '@/shared/api/model';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import CustomerCrmTab from '@/modules/crm/components/CustomerCrmTab';

const { Title, Text } = Typography;

interface CustomerDetailViewProps {
  customer: CustomerDetailDTO;
  onEdit: () => void;
  onViewCustomer?: (branch: string, custNo: string) => void;
  onAddMember?: () => void;
  onCreateMember?: () => void;
  onRemoveMember?: (memberCustNo: string) => void;
  memberActionLoading?: boolean;
  canAddMember?: boolean;
  loading?: boolean;
}

const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({
  customer,
  onEdit,
  onViewCustomer,
  onAddMember,
  onCreateMember,
  onRemoveMember,
  memberActionLoading,
  canAddMember,
  loading,
}) => {
  const selectQueryOptions = {
    query: {
      enabled: !!customer.categoryId,
      staleTime: 5 * 60 * 1000,
    },
  };

  const { data: titleOptions = [] } = useGetSelectFieldValues(
    'title',
    { fieldName: 'ctitle', entityType: 'customer', categoryId: customer.categoryId },
    selectQueryOptions,
  );

  const { data: maritalOptions = [] } = useGetSelectFieldValues(
    'marital_status',
    { fieldName: 'marital', entityType: 'customer', categoryId: customer.categoryId },
    selectQueryOptions,
  );

  const { data: housingOptions = [] } = useGetSelectFieldValues(
    'housing_type',
    { fieldName: 'housing', entityType: 'customer', categoryId: customer.categoryId },
    selectQueryOptions,
  );

  const { data: educationOptions = [] } = useGetSelectFieldValues(
    'education_level',
    { fieldName: 'educatio', entityType: 'customer', categoryId: customer.categoryId },
    selectQueryOptions,
  );

  const { data: incomeOptions = [] } = useGetSelectFieldValues(
    'income',
    { fieldName: 'income', entityType: 'customer', categoryId: customer.categoryId },
    selectQueryOptions,
  );

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  };

  const formatDisplayValue = (value: unknown, fallback = '-'): string => {
    const normalized = normalizeText(value);
    return normalized || fallback;
  };

  const resolveSelectLabel = (value: unknown, options: SelectFieldOption[]): string => {
    const normalizedValue = normalizeText(value);
    if (!normalizedValue) {
      return '-';
    }

    const matchedOption = options.find((option) => normalizeText(option.value) === normalizedValue);
    return formatDisplayValue(matchedOption?.label || matchedOption?.description || normalizedValue);
  };

  const resolveLocationLabel = (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      return formatDisplayValue(record.name ?? record.label ?? record.description ?? record.code ?? record.id);
    }
    return formatDisplayValue(value);
  };

  const getDocumentPreviewUrl = (fileId?: string) => (fileId ? `/api/files/preview/documents/${fileId}` : undefined);

  const getDocumentDownloadUrl = (fileUrl?: string, fileId?: string) => fileUrl || (fileId ? `/api/files/download/documents/${fileId}` : undefined);

  const normalizedCustomerType = (() => {
    const explicitType = normalizeText(customer.customerType).toUpperCase();
    if (explicitType) {
      return explicitType;
    }

    switch (normalizeText(customer.customerData?.custType).toUpperCase()) {
      case 'IND':
        return 'INDIVIDUAL';
      case 'MEM':
        return 'MEMBER';
      case 'GRP':
        return 'GROUP';
      case 'BUS':
        return 'BUSINESS';
      default:
        return '';
    }
  })();

  const isGroupCustomer = normalizedCustomerType === 'GROUP' || normalizeText(customer.customerData?.custType).toUpperCase() === 'GRP';

  const getCustomerTypeTag = (custType?: string) => {
    switch ((custType || normalizedCustomerType).toUpperCase()) {
      case 'INDIVIDUAL':
        return <Tag color="blue" icon={<UserOutlined />}>Individual</Tag>;
      case 'MEMBER':
        return <Tag color="orange" icon={<UserOutlined />}>Member</Tag>;
      case 'GROUP':
        return <Tag color="green" icon={<TeamOutlined />}>Group</Tag>;
      case 'BUSINESS':
        return <Tag color="purple" icon={<BankOutlined />}>Business</Tag>;
      default:
        return <Tag>{custType || normalizedCustomerType || customer.customerData?.custType || 'Unknown'}</Tag>;
    }
  };

  const renderBasicInfo = () => (
    <Card title={isGroupCustomer ? 'Group Information' : 'Basic Information'} extra={getCustomerTypeTag(customer.customerType)}>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Customer Number">
          <Text strong>{`${formatDisplayValue(customer.customerData?.branch, '')}-${formatDisplayValue(customer.customerData?.custNo, '')}`}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Category">
          {formatDisplayValue(customer.categoryName, 'Not Assigned')}
        </Descriptions.Item>
        <Descriptions.Item label={isGroupCustomer ? 'Group Name' : 'Full Name'}>{formatDisplayValue(customer.customerData?.name)}</Descriptions.Item>
        {isGroupCustomer ? (
          <Descriptions.Item label="Group Code">
            <Tag color="green">{formatDisplayValue(customer.customerData?.groupCode || customer.customerData?.custNo)}</Tag>
          </Descriptions.Item>
        ) : (
          <Descriptions.Item label="Date of Birth">
            {customer.customerData?.dobirth ? new Date(customer.customerData.dobirth).toLocaleDateString() : '-'}
          </Descriptions.Item>
        )}
        {isGroupCustomer ? (
          <Descriptions.Item label="Members">
            <Text>{customer.groupMembers?.length ?? 0}</Text>
          </Descriptions.Item>
        ) : (
          <Descriptions.Item label="Gender">{formatDisplayValue(customer.customerData?.custSex)}</Descriptions.Item>
        )}
        <Descriptions.Item label={isGroupCustomer ? 'Group Name Alias' : 'ID Card'}>
          {formatDisplayValue(isGroupCustomer ? customer.customerData?.groupName : customer.customerData?.idCard)}
        </Descriptions.Item>
        <Descriptions.Item label="Phone">{formatDisplayValue(customer.customerData?.phone)}</Descriptions.Item>
        <Descriptions.Item label="Mobile">{formatDisplayValue(customer.customerData?.mobile)}</Descriptions.Item>
        {isGroupCustomer && (
          <Descriptions.Item label="Leader Present">
            {(customer.groupMembers || []).some((member) => member.isLeader) ? <Tag color="gold">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={isGroupCustomer ? 'Group Address' : 'Address'} span={2}>{formatDisplayValue(customer.customerData?.groupAddress || customer.customerData?.address)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  const renderGroupSummary = () => {
    if (!isGroupCustomer) {
      return null;
    }

    return (
      <Card title={<Space><ApartmentOutlined />Group Summary</Space>}>
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <Statistic title="Total Members" value={customer.groupMembers?.length ?? 0} />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Statistic
              title="Leaders"
              value={(customer.groupMembers || []).filter((member) => member.isLeader).length}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Statistic
              title="Member Products"
              value={(customer.groupMembers || []).reduce((sum, member) => sum + (member.productCount || 0), 0)}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  const renderPersonalDetails = () => {
    if (!customer.custInfoData) {
      return <Empty description={isGroupCustomer ? 'No additional group details available' : 'No personal details available'} />;
    }

    return (
      <Card title={isGroupCustomer ? 'Additional Details' : 'Personal Details'}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Title">{resolveSelectLabel(customer.custInfoData.ctitle, titleOptions)}</Descriptions.Item>
          <Descriptions.Item label="Marital Status">{resolveSelectLabel(customer.custInfoData.marital, maritalOptions)}</Descriptions.Item>
          <Descriptions.Item label="Housing Type">{resolveSelectLabel(customer.custInfoData.housing, housingOptions)}</Descriptions.Item>
          <Descriptions.Item label="Education Level">{resolveSelectLabel(customer.custInfoData.educatio, educationOptions)}</Descriptions.Item>
          <Descriptions.Item label="Income Level">{resolveSelectLabel(customer.custInfoData.income, incomeOptions)}</Descriptions.Item>
          <Descriptions.Item label="Next of Kin 1">{formatDisplayValue(customer.custInfoData.kin1)}</Descriptions.Item>
          <Descriptions.Item label="Next of Kin 2">{formatDisplayValue(customer.custInfoData.kin2)}</Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  const renderBiometrics = () => (
    <Card title={<Space><CameraOutlined />Biometric Information</Space>}>
      <Row gutter={16}>
        <Col span={12}>
          <div style={{ textAlign: 'center' }}>
            <Title level={5}>Customer Photo</Title>
            {customer.photoFileId ? (
              <Image
                src={getDocumentPreviewUrl(customer.photoFileId)}
                alt="Customer Photo"
                style={{ maxWidth: '100%', maxHeight: 300 }}
              />
            ) : (
              <Empty description="No photo uploaded" />
            )}
          </div>
        </Col>
        <Col span={12}>
          <div style={{ textAlign: 'center' }}>
            <Title level={5}>Customer Signature</Title>
            {customer.signatureFileId ? (
              <Image
                src={getDocumentPreviewUrl(customer.signatureFileId)}
                alt="Customer Signature"
                style={{ maxWidth: '100%', maxHeight: 300 }}
              />
            ) : (
              <Empty description="No signature uploaded" />
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );

  const getProductBalance = (glNo: string): number | undefined => {
    const balances = customer.dynamicFieldData?.productBalances as Record<string, number> | undefined;
    return balances?.[glNo];
  };

  const getProductDisplayName = (product: NonNullable<CustomerDetailDTO['products']>[number], index: number) => {
    const productName = formatDisplayValue(product.productName, '');
    if (productName && productName !== product.glNo) {
      return productName;
    }
    return formatDisplayValue(product.productTypeName || product.productType || product.glNo, `Product ${index + 1}`);
  };

  const renderProducts = () => (
    <Card title={<Space><BankOutlined />Products & Services</Space>}>
      {customer.products && customer.products.length > 0 ? (
        <Descriptions bordered column={1}>
          {customer.products.map((product, index) => {
            const balance = product.glNo ? getProductBalance(product.glNo) : undefined;
            return (
              <Descriptions.Item key={index} label={`Product ${index + 1}`}>
                <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                  <Col>
                    <Space direction="vertical">
                      <Text strong>{getProductDisplayName(product, index)}</Text>
                      <Text type="secondary">{product.glNo}</Text>
                      {(product.productTypeName || product.productType) && <Tag>{product.productTypeName || product.productType}</Tag>}
                    </Space>
                  </Col>
                  {balance !== undefined && (
                    <Col>
                      <Statistic
                        title="Balance (TZS)"
                        value={balance}
                        precision={2}
                        valueStyle={{ color: balance >= 0 ? '#3f8600' : '#cf1322' }}
                      />
                    </Col>
                  )}
                </Row>
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      ) : (
        <Empty description="No products assigned" />
      )}
    </Card>
  );

  const renderLocations = () => (
    <Card title={<Space><EnvironmentOutlined />Locations</Space>}>
      {customer.locations && customer.locations.length > 0 ? (
        <Descriptions bordered column={1}>
          {customer.locations.map((location, index) => (
            <Descriptions.Item key={index} label={location.addressType || `Location ${index + 1}`}>
              <Space direction="vertical">
                {location.houseNumber && <Text>House: {formatDisplayValue(location.houseNumber)}</Text>}
                {location.street && <Text>Street: {resolveLocationLabel(location.street)}</Text>}
                {location.ward && <Text>Ward: {resolveLocationLabel(location.ward)}</Text>}
                {location.district && <Text>District: {resolveLocationLabel(location.district)}</Text>}
                {location.region && <Text>Region: {resolveLocationLabel(location.region)}</Text>}
                {location.isPrimary && <Tag color="green">Primary</Tag>}
              </Space>
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <Empty description="No locations recorded" />
      )}
    </Card>
  );

  const renderDocuments = () => (
    <Card title={<Space><FileTextOutlined />Documents</Space>}>
      {customer.documents && customer.documents.length > 0 ? (
        <Descriptions bordered column={1}>
          {customer.documents.map((doc, index) => (
            <Descriptions.Item key={index} label={doc.documentTypeName}>
              <Space direction="vertical">
                <Text>{formatDisplayValue(doc.fileName)}</Text>
                {doc.uploadedAt && (
                  <Text type="secondary">
                    Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                  </Text>
                )}
                {doc.isRequired && <Tag color="red">Required</Tag>}
                {getDocumentDownloadUrl(doc.fileUrl, doc.fileId) && (
                  <Button type="link" href={getDocumentDownloadUrl(doc.fileUrl, doc.fileId)} target="_blank">
                    View Document
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : (
        <Empty description="No documents uploaded" />
      )}
    </Card>
  );

  const renderGroupMembers = () => {
    return (
      <Card
        title={<Space><TeamOutlined />Group Members</Space>}
        extra={onAddMember || onCreateMember ? (
          <Space>
            {onCreateMember ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreateMember}
                loading={memberActionLoading}
                disabled={!canAddMember}
              >
                Create New Member
              </Button>
            ) : null}
            {onAddMember ? (
              <Button
                onClick={onAddMember}
                loading={memberActionLoading}
                disabled={!canAddMember}
              >
                Add Existing Individual
              </Button>
            ) : null}
          </Space>
        ) : null}
      >
        {customer.groupMembers && customer.groupMembers.length > 0 ? (
          <List
            dataSource={customer.groupMembers}
            renderItem={(member) => (
              <List.Item
                actions={[
                  ...(member.branch && member.custNo && onViewCustomer ? [
                    <Button
                      key={`view-${member.branch}-${member.custNo}`}
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => onViewCustomer(member.branch!, member.custNo!)}
                    >
                      View Member
                    </Button>,
                  ] : []),
                  ...(member.custNo && onRemoveMember ? [
                    <Popconfirm
                      key={`remove-${member.custNo}`}
                      title="Convert member to individual"
                      description="This will detach the member from the group and make them an individual customer. Continue?"
                      okText="Convert"
                      okButtonProps={{ danger: true, loading: memberActionLoading }}
                      onConfirm={() => onRemoveMember(member.custNo!)}
                      disabled={!!member.isLeader || memberActionLoading}
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={!!member.isLeader || memberActionLoading}
                      >
                        Make Individual
                      </Button>
                    </Popconfirm>,
                  ] : []),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{formatDisplayValue(member.name)}</Text>
                      {member.isLeader && <Tag color="gold">Leader</Tag>}
                      {member.status && <Tag>{formatDisplayValue(member.status)}</Tag>}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text type="secondary">{`${formatDisplayValue(member.branch, '')}-${formatDisplayValue(member.custNo, '')}`}</Text>
                      {member.phone && <Text>Phone: {formatDisplayValue(member.phone)}</Text>}
                      {member.mobile && <Text>Mobile: {formatDisplayValue(member.mobile)}</Text>}
                      <Text type="secondary">Products: {member.productCount ?? 0}</Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description="No group members" />
        )}
      </Card>
    );
  };

  return (
    <div className="customer-detail-view">
      <Card
        title={
          <Space>
            <UserOutlined />
            <span>Customer Details</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<EditOutlined />} onClick={onEdit} loading={loading}>
            Modify / Update
          </Button>
        }
      >
        <Tabs 
          defaultActiveKey="basic"
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {renderBasicInfo()}
                  {renderPersonalDetails()}
                  {renderGroupSummary()}
                </Space>
              ),
            },
            {
              key: 'biometrics',
              label: 'Biometrics',
              children: renderBiometrics(),
            },
            {
              key: 'products',
              label: 'Products',
              children: renderProducts(),
            },
            {
              key: 'locations',
              label: 'Locations',
              children: renderLocations(),
            },
            {
              key: 'documents',
              label: 'Documents',
              children: renderDocuments(),
            },
            ...(isGroupCustomer ? [{
              key: 'members',
              label: 'Members',
              children: renderGroupMembers(),
            }] : []),
            {
              key: 'crm',
              label: <Space><ContactsOutlined />CRM</Space>,
              children: customer.customerData?.branch && customer.customerData?.custNo ? (
                <CustomerCrmTab
                  branch={customer.customerData.branch}
                  custNo={customer.customerData.custNo}
                />
              ) : null,
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default CustomerDetailView;
